import { defineStore, acceptHMRUpdate } from 'pinia';
import { computed, reactive, ref } from 'vue';

export const useCartStore = defineStore('cart', () => {
    // 所有业务购物车容器
    // normal：原来的普通购物车
    // consume：消费购物车
    const carts = reactive({
        normal: {
            // 购物车项，存储所有商品、教练、赠品等信息，key 是项的唯一标识
            items: reactive({}),
            // 购物车商品的顺序数组，决定显示的顺序
            itemOrder: [],
            // 选择打印小票 / 下单即送达
            selectedOptionKey: reactive({
                print: true,
                delivery: true
            })
        },
        consume: {
            // 消费购物车项
            items: reactive({}),
            // 消费购物车顺序数组
            itemOrder: [],
            // 消费购物车选项
            selectedOptionKey: reactive({
                print: true,
                delivery: true
            })
        }
    });

    // 版本号，每次数据更改时增加
    const version = ref(0);

    // 版本更新函数
    const bump = () => (version.value += 1);

    // 获取指定购物车状态
    // cartType:
    // - normal：普通购物车
    // - consume：消费购物车
    const getCartState = (cartType = 'normal') => {
        return carts[cartType] || carts.normal;
    };

    // 根据商品类型生成唯一的 key
    const waresKeyOf = (tabKey, itemId, skuId) => `${tabKey}:${itemId}:${skuId}`;

    // 根据教练 ID 和服务 ID 生成唯一的教练计数 key
    const coachCountKeyOf = (coachId, serviceId) => `${coachId}:${serviceId}`;

    // 根据礼品 ID 和教练 ID 生成礼品 key
    const giftKeyOf = (giftId, coachId) => `${giftId}:${coachId || 0}`;

    // 生成教练时长的唯一 ID
    const genCoachDurationId = () => `coach_${Date.now()}_${Math.random().toString(16).slice(2)}`;

    // 价格处理：转换为有效的数字
    const toPrice = (v) => {
        const n = Number(v); // 将传入的值转换为数字
        return Number.isFinite(n) ? n : 0; // 如果是有效数字，返回该数字，否则返回 0
    };

    // 选择有效价格：在多个候选价格中选择有效的价格
    const pickPrice = (...cands) => {
        for (const v of cands) {
            // 遍历候选价格
            const n = Number(v); // 将价格转换为数字
            if (Number.isFinite(n)) return n; // 返回第一个有效的数字
        }
        return 0; // 如果没有有效的数字，返回 0
    };

    // 更新 itemOrder 顺序数组的方法
    // cartType: 购物车类型
    // key: 要操作的购物车项 key（商品、教练、礼品等）
    // options:
    //   addToTop: 是否将新增的 key 放到数组开头（默认 false）
    //   remove: 是否从数组中删除 key（默认 false）
    const updateOrder = (cartType, key, { addToTop = false, remove = false } = {}) => {
        const cart = getCartState(cartType);
        const order = cart.itemOrder;

        // 查找 key 在 itemOrder 数组中的索引位置
        const idx = order.indexOf(key);

        // 如果是删除操作，且 key 存在于数组中
        if (remove && idx >= 0) {
            order.splice(idx, 1); // 从数组中删除该 key
        }
        // 如果不是删除操作，且 key 不在数组中
        else if (!remove && idx === -1) {
            // 新增 key：根据 addToTop 决定放在数组开头还是末尾
            addToTop
                ? order.unshift(key) // 放在开头
                : order.push(key); // 放在末尾
        }

        // 注意：如果 key 已存在且不是删除操作，则不做任何操作（保持原顺序）
    };

    // 商品增删改：更新商品数量或删除商品
    const patchWares = ({ cartType = 'normal', tabKey, itemId, skuId, sku, item, remark, delta, attr }) => {
        const cart = getCartState(cartType);
        const items = cart.items;

        const key = waresKeyOf(tabKey, itemId, skuId); // 根据商品信息生成唯一的 key
        const existing = items[key]; // 获取购物车中现有的商品

        // 优先用传入sku，否则用旧的sku（左侧 dec 不传 sku 就靠旧的）
        const realSku = sku || existing?.sku || null;

        // 有 sku 才限制上限；没有 sku 就不做上限限制
        const stock = realSku ? Math.max(0, Number(realSku?.stock ?? 0)) : Infinity;

        const curr = Number(existing?.count || 0); // 获取当前商品数量
        const next = Math.min(stock, Math.max(0, curr + Number(delta || 0))); // 计算更新后的商品数量

        if (next <= 0) {
            delete items[key]; // 如果数量小于等于 0，从购物车中删除该商品
            updateOrder(cartType, key, {
                remove: true
            }); // 从顺序数组中删除该商品
            bump(); // 更新版本号
            return;
        }

        // 新增才置顶；更新不动
        updateOrder(cartType, key, {
            addToTop: !existing
        });

        items[key] = {
            type: 'wares', // 设置商品类型为 'wares'
            key, // 唯一标识符
            tabKey, // 商品标签键
            itemId, // 商品 ID
            skuId, // SKU ID
            attr, // 商品属性
            count: next, // 更新后的数量
            remark: remark ?? existing?.remark ?? '', // 商品备注
            item: item || existing?.item || null, // 商品信息
            sku: realSku, // SKU 信息
            updatedAt: Date.now() // 更新时间
        };

        bump(); // 更新版本号
    };

    // 教练时长新增：为购物车添加教练时长项目
    const addCoachDurationLine = (payload, cartType = 'normal') => {
        const cart = getCartState(cartType);
        const items = cart.items;

        const svc = payload?.services?.[0]; // 获取服务信息
        if (!payload?.coachId || !svc?.serviceId) return; // 如果没有教练 ID 或服务 ID，返回

        // 使用 genCoachDurationId 生成唯一的 key
        const key = genCoachDurationId();

        // 新增时，置顶
        updateOrder(cartType, key, {
            addToTop: true
        });

        // 将教练时长信息保存到 items 中
        items[key] = {
            type: 'coachDuration', // 商品类型为 'coachDuration'
            key, // 唯一标识符
            payMode: 'duration', // 支付模式
            coachId: payload.coachId, // 教练 ID
            coach: payload.coach || null, // 教练信息
            service: {
                serviceId: svc.serviceId, // 服务 ID
                serviceName: svc.serviceName, // 服务名称
                price: svc.price, // 服务价格
                unitName: svc.unitName // 服务单位
            },
            createdAt: Date.now() // 创建时间，用于排序
        };

        bump(); // 更新版本号
    };

    // 教练时长删除：删除购物车中的教练时长项
    const removeCoachDurationLine = (key, cartType = 'normal') => {
        const cart = getCartState(cartType);
        const items = cart.items;

        if (items[key]) {
            delete items[key]; // 从 items 中删除
            updateOrder(cartType, key, {
                remove: true
            }); // 从顺序数组中删除
        }
        bump(); // 更新版本号
    };

    // 教练计数增删改：更新教练数量
    const patchCoachCount = ({ cartType = 'normal', coachId, coach, service, delta }) => {
        const cart = getCartState(cartType);
        const items = cart.items;

        const serviceId = service?.serviceId; // 获取服务 ID
        if (!coachId || !serviceId) return; // 如果没有教练 ID 或服务 ID，返回

        const key = coachCountKeyOf(coachId, serviceId); // 生成教练计数的唯一标识符
        const existing = items[key]; // 获取当前购物车中的该教练计数项
        const curr = existing?.count || 0; // 获取当前数量
        const next = Math.max(0, curr + (delta || 0)); // 计算更新后的数量

        if (next <= 0) {
            delete items[key]; // 如果数量小于等于 0，从购物车中删除
            // 从顺序数组中删除该项
            updateOrder(cartType, key, {
                remove: true
            });
            bump(); // 更新版本号
            return;
        }

        updateOrder(cartType, key, {
            addToTop: !existing
        }); // 确保新增的教练计数项置顶

        // 更新教练计数项
        items[key] = {
            type: 'coachCount', // 类型为 'coachCount'
            key, // 唯一标识符
            payMode: 'count', // 支付模式
            coachId, // 教练 ID
            coach: coach || existing?.coach || null, // 教练信息
            service: {
                serviceId, // 服务 ID
                serviceName: service?.serviceName ?? existing?.service?.serviceName, // 服务名称
                price: service?.price ?? existing?.service?.price, // 服务价格
                unitName: service?.unitName ?? existing?.service?.unitName // 服务单位
            },
            count: next, // 更新后的数量
            updatedAt: Date.now() // 更新时间
        };

        bump(); // 更新版本号
    };

    // 礼品增删改：更新礼品的数量
    const patchGift = ({ cartType = 'normal', giftId, gift, coachId, coach, delta }) => {
        const cart = getCartState(cartType);
        const items = cart.items;

        if (!giftId) return; // 如果没有礼品 ID，直接返回

        const key = giftKeyOf(giftId, coachId); // 根据礼品 ID 和教练 ID 生成唯一的 key
        const existing = items[key]; // 获取当前购物车中的礼品项
        // const stock = Math.max(0, Number(gift?.gift_count ?? existing?.gift?.gift_count ?? 0)); // 获取库存数量
        const stock = 99999; // 礼物不需要库存，现在默认最大库存99999
        const curr = existing?.count || 0; // 获取当前数量
        const next = Math.min(stock, Math.max(0, curr + (delta || 0))); // 计算更新后的数量

        if (next <= 0) {
            delete items[key]; // 如果数量小于等于 0，从购物车中删除该礼品
            updateOrder(cartType, key, {
                remove: true
            }); // 从顺序数组中删除
            bump(); // 更新版本号
            return;
        }

        updateOrder(cartType, key, {
            addToTop: !existing
        }); // 确保新增的礼品项置顶

        // 更新礼品项
        items[key] = {
            type: 'gift', // 类型为 'gift'
            key, // 唯一标识符
            giftId, // 礼品 ID
            gift: gift || existing?.gift || null, // 礼品信息
            coachId: coachId || existing?.coachId || null, // 教练 ID
            coach: coach || existing?.coach || null, // 教练信息
            count: next, // 更新后的数量
            updatedAt: Date.now() // 更新时间
        };

        bump(); // 更新版本号
    };

    // 对外查询方法：获取当前 SKU 的库存
    const getSkuStock = (sku) => Math.max(0, Number(sku?.stock ?? 0));

    // 获取指定商品 SKU 的数量
    // cartType: 购物车类型（normal / consume）
    // tabKey: 商品类别/标签
    // itemId: 商品 ID
    // skuId: 商品 SKU ID
    // 返回该 SKU 在购物车中的数量，如果没有则返回 0
    const getSkuCount = (cartType = 'normal', tabKey, itemId, skuId) =>
        // 通过 waresKeyOf 生成该商品的唯一 key，然后从 items 中取 count
        getCartState(cartType).items[waresKeyOf(tabKey, itemId, skuId)]?.count || 0;

    // 获取指定商品的总数量（不区分 SKU）
    // cartType: 购物车类型（normal / consume）
    // tabKey: 商品类别/标签
    // itemId: 商品 ID
    // 返回购物车中所有该商品的数量总和
    const getItemTotalCount = (cartType = 'normal', tabKey, itemId) => {
        // 遍历购物车所有 items，并累计数量
        return Object.values(getCartState(cartType).items).reduce(
            (sum, i) =>
                //tabKey 和 itemId 匹配的累加数量   ，  coach教练匹配coachId  ，商品匹配itemId
                tabKey == 'coach' ? (i.coachId === itemId ? sum + 1 : sum) : i.type === 'wares' && i.tabKey === tabKey && i.itemId === itemId ? sum + i.count : sum,
            0 // reduce 的初始值为 0
        );
    };

    // 弹窗确认入口：根据弹窗的类型进行商品、教练、赠品的增删改
    const applyFromPopup = (payload, cartType = 'normal') => {
        if (!payload?.type) return; // 如果没有类型，则返回

        // 商品类型为 'wares'
        if (payload.type === 'wares') {
            const delta = Number(payload.count) > 0 ? Number(payload.count) : 1;
            // 如果 count 大于 0，使用该值，否则默认增减 1
            patchWares({
                cartType,
                tabKey: payload.tabKey,
                itemId: payload.itemId,
                skuId: payload.skuId,
                sku: payload.sku,
                item: payload.item,
                remark: payload.remark,
                attr: payload.attr,
                delta
            });
            return;
        }

        // 如果是教练类型
        if (payload.type === 'coach') {
            if (payload.payMode === 'duration') {
                // 如果支付模式是 'duration'，则添加教练时长项目
                addCoachDurationLine(payload, cartType);
            } else {
                // 否则更新教练计数
                const svc = payload?.services?.[0];
                patchCoachCount({
                    cartType,
                    coachId: payload.coachId,
                    coach: payload.coach,
                    service: svc,
                    delta: Number(svc?.delta ?? 1)
                });
            }
            return;
        }

        // 如果是赠品类型
        if (payload.type === 'gift') {
            const delta = Number(payload.count) > 0 ? Number(payload.count) : 1;
            // 如果 count 大于 0，使用该值，否则默认增减 1
            patchGift({
                cartType,
                giftId: payload.giftId || payload.gift?.id,
                gift: payload.gift,
                coachId: payload.coachId,
                coach: payload.coach,
                delta
            });
        }
    };

    // 左侧加减删入口：增减或删除购物车商品或服务
    const applyAction = (action, cartType = 'normal') => {
        if (!action?.type || !action?.op) return;
        // 如果没有类型或操作，则返回

        const cart = getCartState(cartType);
        const items = cart.items;
        const attr = action?.attr;
        const step = Math.abs(Number(action.step || 1)); // 获取步长，确保步长为正

        if (action.type === 'wares') {
            const { tabKey, itemId, skuId } = action; // 获取商品的 tabKey、itemId、skuId
            if (!tabKey || !itemId || !skuId) return; // 如果缺少必要的信息，则返回

            // 增加商品数量
            if (action.op === 'inc') {
                patchWares({
                    attr,
                    cartType,
                    tabKey,
                    itemId,
                    skuId,
                    sku: action.sku,
                    item: action.item,
                    remark: action.remark,
                    delta: step
                });
            }

            // 减少商品数量
            if (action.op === 'dec') {
                patchWares({
                    attr,
                    cartType,
                    tabKey,
                    itemId,
                    skuId,
                    delta: -step
                });
            }

            // 删除商品
            if (action.op === 'remove') {
                const key = waresKeyOf(tabKey, itemId, skuId);
                if (items[key]) delete items[key]; // 删除购物车中的该商品
                updateOrder(cartType, key, {
                    remove: true
                }); // 从顺序数组中删除
                bump(); // 更新版本号
            }

            // 只更新备注
            if (action.op === 'remark') {
                const key = waresKeyOf(tabKey, itemId, skuId);
                const existing = items[key];
                if (!existing) return; // 如果该商品不存在，则返回

                patchWares({
                    attr,
                    cartType,
                    tabKey,
                    itemId,
                    skuId,
                    remark: String(action.remark ?? ''), // 更新备注
                    delta: 0 // 数量不变
                });
            }
            return;
        }

        // 教练时长相关操作
        if (action.type === 'coachDuration') {
            if (action.op === 'remove' && action.lineId) {
                removeCoachDurationLine(action.lineId, cartType); // 删除教练时长项
            }
            return;
        }

        // 教练计数相关操作
        if (action.type === 'coachCount') {
            const { coachId, serviceId } = action;
            if (!coachId || !serviceId) return; // 如果缺少 coachId 或 serviceId，返回

            // 增加教练数量
            if (action.op === 'inc') {
                patchCoachCount({
                    cartType,
                    coachId,
                    coach: action.coach,
                    service: action.service || {
                        serviceId
                    },
                    delta: step
                });
            }

            // 减少教练数量
            if (action.op === 'dec') {
                patchCoachCount({
                    cartType,
                    coachId,
                    service: {
                        serviceId
                    },
                    delta: -step
                });
            }

            // 删除教练数量
            if (action.op === 'remove') {
                const key = coachCountKeyOf(coachId, serviceId);
                if (items[key]) delete items[key]; // 删除购物车中的该教练计数项
                updateOrder(cartType, key, {
                    remove: true
                }); // 从顺序数组中删除
                bump(); // 更新版本号
            }
            return;
        }

        // 赠品相关操作
        if (action.type === 'gift') {
            const { giftId, coachId } = action;
            if (!giftId) return; // 如果没有 giftId，返回

            const key = giftKeyOf(giftId, coachId);

            // 增加赠品数量
            if (action.op === 'inc') {
                patchGift({
                    cartType,
                    giftId,
                    coachId,
                    delta: step,
                    gift: action.gift || items[key]?.gift,
                    coach: action.coach || items[key]?.coach
                });
            }

            // 减少赠品数量
            if (action.op === 'dec') {
                patchGift({
                    cartType,
                    giftId,
                    coachId,
                    delta: -step,
                    gift: items[key]?.gift,
                    coach: items[key]?.coach
                });
            }

            // 删除赠品
            if (action.op === 'remove') {
                if (items[key]) delete items[key]; // 删除购物车中的该赠品
                updateOrder(cartType, key, {
                    remove: true
                }); // 从顺序数组中删除
                bump(); // 更新版本号
            }
        }
    };

    // 获取指定购物车的分组数据：根据商品类型将购物车商品分组
    const getGrouped = (cartType = 'normal') => {
        const cart = getCartState(cartType);
        const result = {
            wares: [],
            coachDuration: [],
            coachCount: [],
            gift: []
        };

        // 按顺序遍历
        for (const key of cart.itemOrder) {
            const item = cart.items[key];
            if (!item) continue;
            // 将教练计数分到 'coachCount' 分组
            // 将赠品分到 'gift' 分组
            // 将教练时长分到 'coachDuration' 分组
            // 将商品分到 'wares' 分组
            result[item.type]?.push(item);
        }

        return result; // 返回分组后的数据
    };

    // 获取指定购物车是否为空
    const getIsEmpty = (cartType = 'normal') => {
        const cart = getCartState(cartType);
        return cart.itemOrder.length === 0; // 购物车为空则返回 true
    };

    // 获取指定购物车总数量：计算购物车中所有商品的总数量
    const getTotalCount = (cartType = 'normal') => {
        const cart = getCartState(cartType);
        let sum = 0;

        for (const key in cart.items) {
            const item = cart.items[key];
            if (!item) continue;

            if (item.type === 'coachDuration') {
                sum += 1; // 教练时长算 1 个
            } else {
                sum += item.count || 0; // 累加其他商品的数量
            }
        }

        return sum; // 返回总数量
    };

    // 总金额：计算购物车中的总金额
    // 各类型价格计算
    // 每种商品类型都返回：{ price, count }
    const priceHandlers = {
        // 商品
        wares(item) {
            // sku 信息
            const sku = item.sku || {};

            // 商品基础信息
            const wareItem = item.item || {};

            // 通过 pickPrice 选取最终价格
            const price = pickPrice(sku.price, sku.sale_price, sku.sku_price, wareItem.price, wareItem.sale_price, wareItem.shop_price);

            // 商品数量，默认 0
            const count = item.count || 0;

            // 返回统一结构
            return {
                price,
                count
            };
        },

        // 教练时长
        coachDuration(item) {
            // 教练时长的单价
            const price = pickPrice(item.service?.price);

            // 教练时长固定数量为 1
            const count = 1;

            return {
                price,
                count
            };
        },

        // 教练计次
        coachCount(item) {
            // 单次价格
            const price = pickPrice(item.service?.price);

            // 计次数量
            const count = item.count || 0;

            return {
                price,
                count
            };
        },

        // 礼品
        gift(item) {
            // 礼品信息
            const gift = item.gift || {};

            // 礼品价格
            const price = pickPrice(gift.price, gift.gift_price);

            // 礼品数量
            const count = item.count || 0;

            return {
                price,
                count
            };
        }
    };

    // 获取指定购物车总金额
    const getTotalAmount = (cartType = 'normal') => {
        const cart = getCartState(cartType);

        // 总金额初始化为 0
        let sum = 0;

        // 遍历购物车中的所有商品
        const list = Object.values(cart.items);

        // 遍历数组
        for (const item of list) {
            // 防止空值
            if (!item) continue;

            // 根据商品类型获取对应的价格处理函数
            const handler = priceHandlers[item.type];

            // 如果没有对应类型，直接跳过
            if (!handler) continue;

            // 获取该商品的单价和数量
            const { price, count } = handler(item);

            // 数量必须大于 0 才参与计算
            if (count > 0) {
                // toPrice 统一处理金额精度
                const safePrice = toPrice(price);

                // 累加金额
                sum += safePrice * count;
            }
        }

        // 返回总金额
        return sum;
    };

    // 获取指定购物车选择的打印小票 / 下单即送达
    const getSelectedOptionKey = (cartType = 'normal') => {
        const cart = getCartState(cartType);
        return cart.selectedOptionKey;
    };

    // 设置指定购物车选中的选项
    const setOptKey = (key, cartType = 'normal', boo) => {
        const cart = getCartState(cartType);
        cart.selectedOptionKey[key] = boo; // 保存选中的值
        bump();
    };

    // 清空指定购物车
    const clearAll = (cartType = 'normal') => {
        const cart = getCartState(cartType);
        for (const key in cart.items) delete cart.items[key];
        cart.itemOrder = [];
        cart.selectedOptionKey = {
            print: true,
            delivery: true
        };
        bump();
    };

    // =========================
    // 兼容旧页面：默认走 normal 普通购物车
    // 这样以前没改的页面不用动
    // =========================

    // 购物车分组数据
    const grouped = computed(() => getGrouped('normal'));

    // 购物车是否为空
    const isEmpty = computed(() => getIsEmpty('normal'));

    // 总数量
    const totalCount = computed(() => getTotalCount('normal'));

    // 总金额
    const totalAmount = computed(() => getTotalAmount('normal'));

    // 选中的 key（print 或 delivery）
    const selectedOptionKey = computed(() => getSelectedOptionKey('normal'));

    return {
        // 版本号
        version,

        // 全部购物车容器（普通购物车 + 消费购物车）
        carts,

        // 获取当前 SKU 库存
        getSkuStock,

        // 获取 SKU 数量
        getSkuCount,

        // 获取商品总数量
        getItemTotalCount,

        // 获取指定购物车的分组数据
        getGrouped,

        // 获取指定购物车是否为空
        getIsEmpty,

        // 获取指定购物车总数量
        getTotalCount,

        // 获取指定购物车总金额
        getTotalAmount,

        // 获取指定购物车选项（打印或送货）
        getSelectedOptionKey,

        // 设置指定购物车选项
        setOptKey,

        // 弹窗确认选择
        applyFromPopup,

        // 左侧加减删除操作
        applyAction,

        // 清空购物车数据
        clearAll,

        // =========================
        // 兼容旧页面默认 normal
        // =========================

        selectedOptionKey,
        grouped,
        isEmpty,
        totalCount,
        totalAmount
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useCartStore, import.meta.hot));
}
